/**
 * Video optimization utilities for performance and battery life
 */

/**
 * Detects user's network connection quality
 * @returns {Object} Connection information
 */
export const getConnectionInfo = () => {
  const defaultConnection = {
    effectiveType: '4g',
    saveData: false,
    downlink: 10,
    rtt: 100
  };

  if (!('connection' in navigator)) {
    return defaultConnection;
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  
  return {
    effectiveType: connection.effectiveType || '4g',
    saveData: connection.saveData || false,
    downlink: connection.downlink || 10,
    rtt: connection.rtt || 100,
    type: connection.type || 'unknown'
  };
};

/**
 * Determines optimal video preload strategy based on connection
 * @returns {string} Preload strategy ('auto', 'metadata', 'none')
 */
export const getOptimalPreloadStrategy = () => {
  const connection = getConnectionInfo();
  
  // If user has data saver enabled, don't preload
  if (connection.saveData) {
    return 'none';
  }
  
  // Based on effective connection type
  switch (connection.effectiveType) {
    case '4g':
      return connection.downlink > 5 ? 'auto' : 'metadata';
    case '3g':
      return 'metadata';
    case '2g':
    case 'slow-2g':
      return 'none';
    default:
      return 'metadata';
  }
};

/**
 * Checks if device is likely on battery power
 * @returns {Promise<boolean>} True if on battery
 */
export const isOnBattery = async () => {
  if (!('getBattery' in navigator)) {
    return false; // Assume plugged in if we can't detect
  }

  try {
    const battery = await navigator.getBattery();
    return !battery.charging && battery.level < 0.5; // On battery and less than 50%
  } catch (error) {
    console.warn('Battery API not available:', error);
    return false;
  }
};

/**
 * Checks if device prefers reduced motion
 * @returns {boolean} True if reduced motion is preferred
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Detects if device is mobile
 * @returns {boolean} True if mobile device
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Gets optimal video quality based on device and connection
 * @returns {Object} Video quality settings
 */
export const getOptimalVideoSettings = async () => {
  const connection = getConnectionInfo();
  const onBattery = await isOnBattery();
  const isMobile = isMobileDevice();
  const reducedMotion = prefersReducedMotion();

  // If user prefers reduced motion, disable video
  if (reducedMotion) {
    return {
      shouldPlay: false,
      preload: 'none',
      quality: 'low'
    };
  }

  // If on battery and mobile, be conservative
  if (onBattery && isMobile) {
    return {
      shouldPlay: false,
      preload: 'none',
      quality: 'low'
    };
  }

  // If data saver is enabled, be conservative
  if (connection.saveData) {
    return {
      shouldPlay: false,
      preload: 'none',
      quality: 'low'
    };
  }

  // Based on connection quality
  switch (connection.effectiveType) {
    case '4g':
      return {
        shouldPlay: true,
        preload: connection.downlink > 5 ? 'auto' : 'metadata',
        quality: connection.downlink > 10 ? 'high' : 'medium'
      };
    case '3g':
      return {
        shouldPlay: true,
        preload: 'metadata',
        quality: 'medium'
      };
    case '2g':
    case 'slow-2g':
      return {
        shouldPlay: false,
        preload: 'none',
        quality: 'low'
      };
    default:
      return {
        shouldPlay: true,
        preload: 'metadata',
        quality: 'medium'
      };
  }
};

/**
 * Throttle function for performance optimization
 * @param {Function} func Function to throttle
 * @param {number} limit Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Debounce function for performance optimization
 * @param {Function} func Function to debounce
 * @param {number} wait Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Creates a performance observer for video metrics
 * @param {HTMLVideoElement} videoElement Video element to observe
 * @param {Function} callback Callback for performance data
 */
export const createVideoPerformanceObserver = (videoElement, callback) => {
  if (!('PerformanceObserver' in window)) {
    console.warn('PerformanceObserver not supported');
    return null;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.name.includes('video') || entry.entryType === 'measure') {
          callback({
            name: entry.name,
            duration: entry.duration,
            startTime: entry.startTime,
            type: entry.entryType
          });
        }
      });
    });

    observer.observe({ entryTypes: ['measure', 'navigation'] });
    return observer;
  } catch (error) {
    console.warn('Failed to create performance observer:', error);
    return null;
  }
};

/**
 * Measures video loading performance
 * @param {HTMLVideoElement} videoElement Video element
 * @returns {Object} Performance metrics
 */
export const measureVideoPerformance = (videoElement) => {
  if (!videoElement) return null;

  const startTime = performance.now();
  
  return {
    startTime,
    measureLoadTime: () => {
      const loadTime = performance.now() - startTime;
      performance.mark('video-load-complete');
      performance.measure('video-load-duration', 'video-load-start', 'video-load-complete');
      return loadTime;
    },
    getVideoMetrics: () => ({
      videoWidth: videoElement.videoWidth,
      videoHeight: videoElement.videoHeight,
      duration: videoElement.duration,
      currentTime: videoElement.currentTime,
      buffered: videoElement.buffered.length > 0 ? {
        start: videoElement.buffered.start(0),
        end: videoElement.buffered.end(videoElement.buffered.length - 1)
      } : null,
      networkState: videoElement.networkState,
      readyState: videoElement.readyState
    })
  };
};

/**
 * Optimizes video element for performance
 * @param {HTMLVideoElement} videoElement Video element to optimize
 */
export const optimizeVideoElement = (videoElement) => {
  if (!videoElement) return;

  // Set optimal attributes for performance
  videoElement.setAttribute('playsinline', '');
  videoElement.setAttribute('webkit-playsinline', '');
  videoElement.setAttribute('x-webkit-airplay', 'deny');
  
  // Optimize for mobile
  if (isMobileDevice()) {
    videoElement.setAttribute('preload', 'none');
    videoElement.muted = true;
  }

  // Add performance monitoring
  performance.mark('video-load-start');
};

export default {
  getConnectionInfo,
  getOptimalPreloadStrategy,
  isOnBattery,
  prefersReducedMotion,
  isMobileDevice,
  getOptimalVideoSettings,
  throttle,
  debounce,
  createVideoPerformanceObserver,
  measureVideoPerformance,
  optimizeVideoElement
};